
import { defineComponent, computed, PropType } from "vue";
import { Bar as BarChart } from "vue-chartjs";
import {
  Chart as ChartJS,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartData,
} from "chart.js";
import { GroupedAlert, ResultStatus } from "@/stores/telegramAlerts";
import dayjs from "dayjs";
import {
  getStakeBackgroundColors,
  getStakeBorderColors,
} from "./utils/stakeColors";

ChartJS.register(
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

export default defineComponent({
  name: "StakesHistogram",
  components: {
    BarChart,
  },
  props: {
    alerts: {
      type: Array as PropType<GroupedAlert[]>,
      required: true,
    },
  },
  setup(props) {
    // Helper function to create striped pattern
    const createPattern = (color: string) => {
      if (typeof document === "undefined") return color;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return color;

      canvas.width = 10;
      canvas.height = 10;

      // Parse RGBA color
      const match = color.match(
        /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
      );
      if (match) {
        const [, r, g, b, a = "1"] = match;
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
        ctx.fillRect(0, 0, 10, 10);

        // Add diagonal stripes
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${parseFloat(a) * 0.5})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 10);
        ctx.lineTo(10, 0);
        ctx.stroke();
      }

      return ctx.createPattern(canvas, "repeat") || color;
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          title: {
            display: true,
            text: "Día del Mes",
          },
          grid: {
            offset: true,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: "Cantidad de Apuestas",
          },
          ticks: {
            stepSize: 1,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom" as const,
          onClick: (_e: any, legendItem: any, legend: any) => {
            // Prevent interaction with separator
            if (legendItem.text === "―――――") {
              return;
            }
            // Default click behavior for other items
            const index = legendItem.datasetIndex;
            const ci = legend.chart;
            if (ci.isDatasetVisible(index)) {
              ci.hide(index);
              legendItem.hidden = true;
            } else {
              ci.show(index);
              legendItem.hidden = false;
            }
          },
          labels: {
            generateLabels: (chart: any) => {
              const original =
                ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
              // Sort labels: stakes first, then status
              const stakeLabels = original
                .filter((item: any) => item.text.startsWith("Stake"))
                .sort((a: any, b: any) => {
                  const valA = parseFloat(a.text.replace("Stake ", ""));
                  const valB = parseFloat(b.text.replace("Stake ", ""));
                  return valA - valB;
                });
              const statusLabels = original.filter(
                (item: any) => !item.text.startsWith("Stake")
              );

              // Add separator
              if (stakeLabels.length > 0 && statusLabels.length > 0) {
                stakeLabels.push({
                  text: "―――――",
                  fillStyle: "transparent",
                  strokeStyle: "transparent",
                  hidden: false,
                  index: -1,
                  datasetIndex: -1,
                });
              }

              return [...stakeLabels, ...statusLabels];
            },
          },
        },
        tooltip: {
          mode: "index" as const,
          intersect: false,

          callbacks: {
            title: (tooltipItems: any[]) => {
              if (tooltipItems.length > 0) {
                const firstItem = tooltipItems[0];
                const stack = firstItem.dataset.stack;
                const isStake = stack === "stakes";
                return `Día ${firstItem.label} - ${
                  isStake ? "Stakes" : "Resultados"
                }`;
              }
              return "";
            },

            label: (context: any) => {
              // Only show if this item belongs to the hovered stack
              const hoveredElement = context.chart.getActiveElements()[0];
              if (!hoveredElement) return "";

              const hoveredStack =
                context.chart.data.datasets[hoveredElement.datasetIndex].stack;
              const currentStack = context.dataset.stack;

              if (hoveredStack !== currentStack) {
                return null as any; // Hide this item
              }

              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y;
              }
              return label;
            },
            afterTitle: () => {
              return ""; // Remove extra spacing
            },
          },
        },
      },
      categoryPercentage: 0.9,
      barPercentage: 0.8,
    };

    const chartData = computed((): ChartData<"bar", number[], string> => {
      // Get all unique days in the month
      const daysMap = new Map<string, Map<string, number>>();
      const statusMap = new Map<string, Map<ResultStatus, number>>();

      // Determine the number of days in the current month
      let daysInMonth = 31;
      if (props.alerts.length > 0) {
        const firstAlertDate = dayjs(props.alerts[0].mainMessage.date);
        daysInMonth = firstAlertDate.daysInMonth();
      }

      // Process alerts to group by day and stake, and by day and status
      props.alerts.forEach((alert) => {
        const day = dayjs(alert.mainMessage.date).format("D");

        // Process stakes (existing logic)
        if (alert.stake) {
          if (!daysMap.has(day)) {
            daysMap.set(day, new Map<string, number>());
          }

          const stakeMap = daysMap.get(day);
          if (stakeMap) {
            stakeMap.set(alert.stake, (stakeMap.get(alert.stake) || 0) + 1);
          }
        }

        // Process statuses (NEW)
        const status = alert.resultStatus || ResultStatus.PENDING;
        if (!statusMap.has(day)) {
          statusMap.set(day, new Map<ResultStatus, number>());
        }
        const dayStatusMap = statusMap.get(day);
        if (dayStatusMap) {
          dayStatusMap.set(status, (dayStatusMap.get(status) || 0) + 1);
        }
      });

      // Get all unique stakes
      const allStakes = new Set<string>();
      props.alerts.forEach((alert) => {
        if (alert.stake) {
          allStakes.add(alert.stake);
        }
      });

      // Sort stakes for consistent ordering
      const sortedStakes = Array.from(allStakes).sort(
        (a, b) => parseFloat(a) - parseFloat(b)
      );

      // Generate all days of the month (1 to daysInMonth)
      const days = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));

      // Get consistent colors for stakes from shared utility
      const backgroundColors = getStakeBackgroundColors();
      const borderColors = getStakeBorderColors();

      // Create datasets for each stake
      const stakeDatasets = sortedStakes.map((stake, index) => {
        const data = days.map((day) => {
          const stakeMap = daysMap.get(day);
          return stakeMap?.get(stake) || 0;
        });

        return {
          label: `Stake ${stake}`,
          data,
          backgroundColor: backgroundColors[index % backgroundColors.length],
          borderColor: borderColors[index % borderColors.length],
          borderWidth: 1,
          stack: "stakes",
        };
      });

      // Create datasets for status (NEW)
      const statusConfig = [
        {
          status: ResultStatus.WIN,
          label: "Ganadas",
          backgroundColor: "rgba(40, 167, 69, 0.8)",
          borderColor: "rgba(40, 167, 69, 1)",
        },
        {
          status: ResultStatus.LOSS,
          label: "Perdidas",
          backgroundColor: "rgba(220, 53, 69, 0.8)",
          borderColor: "rgba(220, 53, 69, 1)",
        },
        {
          status: ResultStatus.PENDING,
          label: "Pendientes",
          backgroundColor: "rgba(255, 193, 7, 0.8)",
          borderColor: "rgba(255, 193, 7, 1)",
        },
        {
          status: ResultStatus.VOID,
          label: "Anuladas",
          backgroundColor: "rgba(108, 117, 125, 0.8)",
          borderColor: "rgba(108, 117, 125, 1)",
        },
      ];

      const statusDatasets = statusConfig.map((config) => {
        const data = days.map((day) => {
          const dayStatusMap = statusMap.get(day);
          return dayStatusMap?.get(config.status) || 0;
        });

        return {
          label: config.label,
          data,
          backgroundColor: createPattern(config.backgroundColor),
          borderColor: config.borderColor,
          borderWidth: 2,
          stack: "status",
        };
      });

      // Combine both groups of datasets
      return {
        labels: days,
        datasets: [...stakeDatasets, ...statusDatasets],
      };
    });

    return {
      chartData,
      chartOptions,
    };
  },
});
